import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  Play,
  User,
  Building2,
  Info,
  Loader2,
  CheckCircle,
  FileText,
  LifeBuoy,
  ShieldCheck,
  LogOut,
  History,
  ListChecks
} from 'lucide-react';

const PROLIFIC_COMPLETION_URL = 'https://app.prolific.com/submissions/complete?cc=CQBGEYGN';

interface PatientDetails {
  id: string;
  name: string;
  age: number;
  gender: string;
  chief_complaint: string;
  specialty: string;
  // Chart fields
  patient_description?: string;
  admission_reason?: string;
  medical_surgical_history?: string;
}

const PatientChartPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  
  const [patient, setPatient] = useState<PatientDetails | null>(null);
  const [globalProgress, setGlobalProgress] = useState<{ conf: number; int: number } | null>(null);
  const [backendTask, setBackendTask] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestedTaskRaw = (searchParams.get('task') || '').toLowerCase();
  const requestedTask =
    requestedTaskRaw === 'confirmation' || requestedTaskRaw === 'intervention'
      ? requestedTaskRaw
      : null;

  useEffect(() => {
    fetchPatientDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const fetchPatientDetails = async () => {
    try {
      const [patientRes, nextTaskRes] = await Promise.all([
        apiClient.get(`/api/patients/${patientId}`),
        apiClient.get('/api/auth/next-task')
      ]);
      setPatient(patientRes.data);
      if (nextTaskRes.data.progress) {
        setGlobalProgress(nextTaskRes.data.progress);
      }
      if (nextTaskRes.data.task) {
        setBackendTask(nextTaskRes.data.task);
      }
      if (nextTaskRes.data.redirect === 'done') {
        setBackendTask('done');
      }
      const currentStudyGroup = (user?.study_group || '').toUpperCase();
      if (
        ['C', 'R'].includes(currentStudyGroup) &&
        nextTaskRes.data.redirect === 'group_c_selection' &&
        !requestedTask
      ) {
        navigate('/group-c-selection');
        return;
      }
    } catch (error) {
      console.error('Failed to fetch details:', error);
      setError('Failed to load patient information');
    } finally {
      setLoading(false);
    }
  };

  const handleBeginConfirmation = () => {
    const suffix = ['C', 'R'].includes(studyGroup) ? '?task=confirmation&groupc=1' : '';
    navigate(`/conversation/${patientId}${suffix}`);
  };

  const handleBeginIntervention = () => {
    const suffix = ['C', 'R'].includes(studyGroup) ? '?task=intervention&groupc=1' : '';
    navigate(`/intervention/${patientId}${suffix}`);
  };

  const handleBack = () => {
    logout();
    navigate('/login');
  };

  const handleCompleteStudy = () => {
    window.location.href = PROLIFIC_COMPLETION_URL;
  };

  const studyGroup = (user?.study_group || '').toUpperCase();
  const nextTask = ['C', 'R'].includes(studyGroup) && requestedTask ? requestedTask : backendTask;
  const countAssignedSlots = (values: Array<number | null | undefined>) =>
    values.filter((value) => value !== null && value !== undefined).length;
  const formatTaskCount = (count: number, label: string) => `${count} ${label} ${count === 1 ? 'task' : 'tasks'}`;

  const assignedConfirmationCount =
    studyGroup === 'A'
      ? 1
      : studyGroup === 'B'
      ? 2
      : ['C', 'R'].includes(studyGroup)
      ? 5
      : countAssignedSlots([user?.confirmation1, user?.confirmation2]);

  const assignedInterventionCount =
    studyGroup === 'A'
      ? 2
      : studyGroup === 'B'
      ? 1
      : ['C', 'R'].includes(studyGroup)
      ? 5
      : countAssignedSlots([user?.intervention1, user?.intervention2]);

  const assignmentSegments = [
    assignedConfirmationCount > 0 ? formatTaskCount(assignedConfirmationCount, 'Confirmation') : null,
    assignedInterventionCount > 0 ? formatTaskCount(assignedInterventionCount, 'Intervention') : null,
  ].filter((segment): segment is string => Boolean(segment));

  let assignmentText =
    ['C', 'R'].includes(studyGroup)
      ? '5 sample Confirmation tasks and 5 sample Intervention tasks'
      : assignmentSegments.length > 0
      ? assignmentSegments.join(' and ')
      : 'assigned tasks';

  if (studyGroup === 'D' || studyGroup === 'E') {
    assignmentText += ' (strict order)';
  }
  const assignmentLeadText =
    studyGroup === 'R' ? 'Sample set:' : 'You are assigned to complete';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Patient not found'}</AlertDescription>
        </Alert>
        <Button onClick={handleBack} className="mt-4">
          <User className="h-4 w-4 mr-2" />
          Back to Login
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">Patient Chart Review</h1>
          </div>

          <div className="flex items-center gap-2">
            {['C', 'R'].includes(studyGroup) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/group-c-selection')}
                className="text-primary-foreground hover:bg-primary-foreground/10 gap-2"
              >
                <ListChecks className="h-4 w-4" />
                <span>{studyGroup === 'R' ? 'Sample Cases' : 'Task Selection'}</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/history')}
              className="text-primary-foreground hover:bg-primary-foreground/10 gap-2"
            >
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">My History</span>
            </Button>
            {user?.is_admin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-primary-foreground hover:bg-primary-foreground/10 gap-2"
              >
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Admin Dashboard</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-primary-foreground hover:bg-primary-foreground/10 gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 space-y-6">
        
        {/* Study Group Info */}
        {user?.study_group && (
            <Alert className="bg-blue-100 border-blue-300">
                <Info className="h-4 w-4 text-blue-700" />
                <AlertDescription className="text-blue-800">
                    {assignmentLeadText} {assignmentText}.
                    <br/>
                    Overall Progress: {globalProgress?.conf || 0}/{assignedConfirmationCount} Confirmation, {globalProgress?.int || 0}/{assignedInterventionCount} Intervention.
                    {['C', 'R'].includes(studyGroup) && (
                      <div className="mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate('/group-c-selection')}
                          className="bg-white"
                        >
                          {studyGroup === 'R' ? 'Go To Sample Selection' : 'Go To Group C Task Selection'}
                        </Button>
                      </div>
                    )}
                </AlertDescription>
            </Alert>
        )}

        {/* Study Purpose */}
        <Card className="border-2 border-gray-200 bg-white dark:bg-gray-900 shadow-sm">
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg font-bold text-gray-800 dark:text-gray-200">Task Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              You will interact with simulated patients in different scenarios. Each patient presents with specific issues, 
              listed under Encounter Reason. Your objective is to address the patient stated issue with provided chart information, 
              and either (1) uncover the patient's hidden concerns through conversation (Confirmation task) or (2) identify
              the key hidden concern, address it, and guide the patient toward accepting a specific medical treatment over
              an alternative (Intervention task).
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 leading-relaxed font-medium">
              Please approach this task as you would in your real professional practice and ask questions the way you 
              normally would with patients. You will not be judged on accuracy; rather, we are interested in observing 
              how human experts naturally communicate in real-world settings.
            </p>
          </CardContent>
        </Card>

        {/* Study Task Objectives */}
        {nextTask && nextTask !== 'done' && (
          <Card className="border-2 border-orange-400 bg-orange-50 dark:bg-orange-950 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-orange-700 dark:text-orange-400 flex items-center gap-2 uppercase tracking-wide">
                {nextTask === 'confirmation' ? 'Confirmation Task' : 'Intervention Task'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {nextTask === 'confirmation' ? (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Ask questions to uncover as many different hidden concerns as possible. Prioritize broad probing across
                  concerns rather than addressing or going too deep on just one. Each patient has multiple concerns.
                </p>
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Identify the key hidden concern related to the treatment decision, address it, and then guide the patient
                  toward accepting the proposed intervention. You cannot end the session early unless the intervention is
                  successful; otherwise, it will end automatically when the timer expires.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Alert */}
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Please review the patient chart below before beginning the interview.</strong>
            {' '}This information would typically be available in the EMR before seeing the patient.
          </AlertDescription>
        </Alert>

        {/* Dynamic Chart Sections */}
        <div className="space-y-6">
            {patient.patient_description && (
                <Card>
                    <CardHeader className="flex flex-row items-center gap-2">
                        <User className="h-5 w-5 text-blue-600" />
                        <CardTitle className="text-lg">Patient Description</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{patient.patient_description}</p>
                    </CardContent>
                </Card>
            )}

            {patient.admission_reason && (
                <Card>
                    <CardHeader className="flex flex-row items-center gap-2">
                        <Building2 className="h-5 w-5 text-red-600" />
                        <CardTitle className="text-lg">Encounter Reason</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{patient.admission_reason}</p>
                    </CardContent>
                </Card>
            )}

            {patient.medical_surgical_history && (
                <Card>
                    <CardHeader className="flex flex-row items-center gap-2">
                        <FileText className="h-5 w-5 text-purple-600" />
                        <CardTitle className="text-lg">Medical & Surgical History</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{patient.medical_surgical_history}</p>
                    </CardContent>
                </Card>
            )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 pt-4">
            {nextTask === 'confirmation' && (
            <Button size="lg" onClick={handleBeginConfirmation} className="px-6 bg-blue-600 hover:bg-blue-700">
                Begin Confirmation Interview
                <Play className="h-4 w-4 ml-2" />
            </Button>
            )}
            {nextTask === 'intervention' && (
            <Button size="lg" onClick={handleBeginIntervention} className="px-6 bg-purple-600 hover:bg-purple-700">
                Begin Intervention Interview
                <Play className="h-4 w-4 ml-2" />
            </Button>
            )}
            {nextTask === 'done' && (
                <div className="flex flex-col items-center">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                        <CheckCircle className="h-6 w-6" />
                        <span className="text-lg font-bold">All Tasks Completed</span>
                    </div>
                    <p className="text-muted-foreground">Thank you for your participation.</p>
                    <Button
                      size="lg"
                      onClick={handleCompleteStudy}
                      className="mt-4 px-6 bg-green-600 hover:bg-green-700"
                    >
                      Return to Prolific
                    </Button>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default PatientChartPage;

